import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { ResponsiveContainer, LineChart, XAxis, YAxis, Line, CartesianGrid, Tooltip } from 'recharts';

const CustomAxisTick = (props) => {
  const { x, y, payload } = props;

  const formattedDate = moment(payload.value).format('MMM D (LT)');

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} dx={28} textAnchor="end" fill="#666">
        {formattedDate}
      </text>
    </g>
  );
};

function restructureData(formattedDataPoints) {
  const keys = Object.keys(formattedDataPoints);
  const dataArray = [];
  let longest = 0;
  let longestData = [];

  // create initial object with all keys set to null
  const initializeDataPoints = keys.reduce((o, key) => ({ ...o, [key]: null }), {});

  // find the data point with the most data points
  keys.forEach((reg) => {
    if (formattedDataPoints[reg].length > longest) {
      longestData = formattedDataPoints[reg];
      longest = formattedDataPoints[reg].length;
    }
  });

  // add initialized registers with null data, longest data set and dates to dataArray
  longestData.forEach((dataset) => {
    dataArray.push({
      ...initializeDataPoints,
      ...dataset,
    });
  });

  // go through the registers and replace null values if applicable
  keys.forEach((register) => {
    const values = formattedDataPoints[register];
    values.forEach((dataset, index) => {
      if (formattedDataPoints[register].length === longest) {
        // If the data length is the same as the longest data length
        // ie. there is no missing data
        dataArray[index][register] = dataset[register];
      } else {
        const indexOfDate = dataArray.findIndex((element) => {
          const { _i: elDateFormat } = element.date;
          const { _i: datasetDateFormat } = dataset.date;
          return elDateFormat === datasetDateFormat;
        });
        dataArray[indexOfDate][register] = dataset[register];
      }
    });
  });
  return dataArray;
}

export const AlertsChart = (props) => {
  const { data, theme } = props;
  const [formattedData, setFormmattedData] = useState({});
  const [dataPoints, setDataPoints] = useState([]);

  useEffect(() => {
    const formattedDataPoints = {};
    const formattedTags = [];

    data.data_points.map((dataSet) => {
      return formattedTags.push(dataSet.id);
    });

    data.data_points.map((dataSet) => {
      formattedDataPoints[dataSet.id] = dataSet.graph_data.map((d) => ({
        date: d.x,
        [dataSet.id]: d.actual,
      }));

      return formattedDataPoints;
    });

    setDataPoints(formattedTags);
    setFormmattedData(restructureData(formattedDataPoints));
  }, [data]);

  const tooltipFormatter = (value, name) => {
    let metric = '';
    let displayName = '';

    data.data_points.forEach((datapoint) => {
      if (datapoint.id === name) {
        metric = datapoint.unit;
        displayName = datapoint.name;
      }
    });

    return [`${value.toFixed(2)} ${metric}`, displayName];
  };

  const getTooltipStyle = () => {
    const style = theme === 'light' ? { background: '#ffffff' } : { background: '#141414' };
    return style;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={formattedData}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis type="category" height={80} dataKey="date" tick={<CustomAxisTick />} />
        <YAxis type="number" />
        {dataPoints.map((dataPoint) => {
          const lineColors = JSON.parse(sessionStorage.getItem('alertLineColorObj'));
          return (
            <Line key={dataPoint} dataKey={dataPoint} stroke={lineColors[dataPoint]} dot={false} strokeWidth={2} />
          );
        })}
        <Tooltip
          contentStyle={getTooltipStyle()}
          isAnimationActive={false}
          labelFormatter={(label) => moment(label).format('MMM D, YYYY (LT)')}
          formatter={(value, name) => tooltipFormatter(value, name)}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

AlertsChart.propTypes = {
  data: PropTypes.shape({
    date: PropTypes.string,
    interval: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
    data_points: PropTypes.arrayOf(
      PropTypes.shape({
        graph_data: PropTypes.arrayOf(
          PropTypes.shape({
            actual: PropTypes.number,
            x: PropTypes.string,
          }),
        ),
        id: PropTypes.string,
        name: PropTypes.string,
        tag: PropTypes.string,
        unit: PropTypes.string,
      }),
    ),
  }).isRequired,
};
